import { useInfiniteQuery } from 'react-query';
import { useEffect, useState } from 'react';
import { getWinnerItemsService } from 'pages/Raffle/services/raffle';
import { TResponse } from 'types/axios';
import { TGetWinnerItemsResponse, TWinnerItem } from 'types/raffle';

type WinnerItemsCount = {
  isLoadingGetWinningItems: boolean;
  hasErrorGettingWinnerItems: boolean;
  handleGetWinnerItems: () => void;
  winningItems: TWinnerItem[];
  errorMessage: string;
  refetchItems: () => void;
};

function useGetWinnerItems(): WinnerItemsCount {
  const limit = 10;
  const [winningItems, setWinningItems] = useState<TWinnerItem[]>([]);
  const [error, setError] = useState({ hasError: false, errorMessage: '' });

  const {
    data,
    refetch,
    fetchNextPage: fetchNextWinningItems,
    isLoading: isLoadingGetWinningItems,
    hasNextPage,
  } = useInfiniteQuery(
    'getWinnerItems',
    ({ pageParam = 0 }) => {
      return getWinnerItemsService({
        page: pageParam + 1,
        pageSize: limit,
      });
    },
    {
      enabled: false,
      suspense: false,
      getNextPageParam: (_, pages) =>
        pages[pages.length - 1].data.result?.tickets.length >= limit &&
        pages.length,
      onSuccess: (resp) => {
        if (!resp.pages.slice(-1)[0].data.success) {
          setError({
            hasError: true,
            errorMessage: resp.pages.slice(-1)[0].data.errorMessage,
          });
        } else {
          setError({ hasError: false, errorMessage: '' });
        }
      },
    }
  );

  useEffect(() => {
    const winnerItemsList = data?.pages.reduce(
      (acc: TWinnerItem[], current: TResponse<TGetWinnerItemsResponse>) =>
        current.data?.result ? acc.concat(current.data.result.tickets) : acc,
      []
    ) as TWinnerItem[];

    if (winnerItemsList?.length > 0) {
      setWinningItems(winnerItemsList);
    }
  }, [data?.pages.length]);

  function handleGetWinnerItems() {
    if (hasNextPage || !data) {
      fetchNextWinningItems();
    } else if (error.hasError) {
      refetch();
    }
  }

  return {
    isLoadingGetWinningItems,
    hasErrorGettingWinnerItems: error.hasError,
    handleGetWinnerItems,
    winningItems,
    errorMessage: error.errorMessage,
    refetchItems: refetch,
  };
}

export default useGetWinnerItems;
